import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Share2 } from "lucide-react";
import type { Project, Firing, Segment } from "../types";

interface ShareOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
  firings: Firing[];
  segments: Segment[];
  onShare: (options: ShareOptions) => void;
}

export interface ShareOptions {
  includeBeforePhotos: boolean;
  includeDuringPhotos: boolean;
  includeAfterPhotos: boolean;
  includeMaterials: boolean;
  selectedFiringIds: string[];
}

const hasPhotosOfType = (project: Project, type: string): boolean => {
  return (project.photos ?? []).some(
    (p) => p.type?.toLowerCase() === type.toLowerCase()
  );
};

const ShareOptionsDialog = ({
  open,
  onOpenChange,
  project,
  firings,
  segments,
  onShare,
}: ShareOptionsDialogProps) => {
  const hasBefore = useMemo(() => hasPhotosOfType(project, "before"), [project]);
  const hasDuring = useMemo(() => hasPhotosOfType(project, "during"), [project]);
  const hasAfter = useMemo(() => hasPhotosOfType(project, "after"), [project]);
  const hasMaterials = !!project.materials && Object.keys(project.materials).length > 0;
  const hasPhotos = hasBefore || hasDuring || hasAfter;

  const [includeBeforePhotos, setIncludeBeforePhotos] = useState(true);
  const [includeDuringPhotos, setIncludeDuringPhotos] = useState(true);
  const [includeAfterPhotos, setIncludeAfterPhotos] = useState(true);
  const [includeMaterials, setIncludeMaterials] = useState(true);
  const [selectedFiringIds, setSelectedFiringIds] = useState<string[]>(() =>
    firings.filter((f) => f.id).map((f) => f.id!)
  );

  const toggleFiring = (firingId: string) => {
    setSelectedFiringIds((prev) =>
      prev.includes(firingId)
        ? prev.filter((id) => id !== firingId)
        : [...prev, firingId]
    );
  };

  const handleShare = () => {
    onShare({
      includeBeforePhotos,
      includeDuringPhotos,
      includeAfterPhotos,
      includeMaterials,
      selectedFiringIds,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {hasPhotos && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Photos</p>
              {hasBefore && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="share-before"
                    checked={includeBeforePhotos}
                    onCheckedChange={(checked) => setIncludeBeforePhotos(!!checked)}
                  />
                  <Label htmlFor="share-before">Include Before photos</Label>
                </div>
              )}
              {hasDuring && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="share-during"
                    checked={includeDuringPhotos}
                    onCheckedChange={(checked) => setIncludeDuringPhotos(!!checked)}
                  />
                  <Label htmlFor="share-during">Include During photos</Label>
                </div>
              )}
              {hasAfter && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="share-after"
                    checked={includeAfterPhotos}
                    onCheckedChange={(checked) => setIncludeAfterPhotos(!!checked)}
                  />
                  <Label htmlFor="share-after">Include After photos</Label>
                </div>
              )}
            </div>
          )}
          {hasMaterials && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="share-materials"
                checked={includeMaterials}
                onCheckedChange={(checked) => setIncludeMaterials(!!checked)}
              />
              <Label htmlFor="share-materials">Include Materials</Label>
            </div>
          )}
          {firings.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Firings</p>
              {firings.map((firing, index) => (
                firing.id && (
                  <div key={firing.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`share-firing-${firing.id}`}
                      checked={selectedFiringIds.includes(firing.id)}
                      onCheckedChange={() => toggleFiring(firing.id!)}
                    />
                    <Label htmlFor={`share-firing-${firing.id}`}>
                      {firing.name || `Firing ${index + 1}`}
                    </Label>
                  </div>
                )
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Fields related to costs, time taken, or status will not be shared.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={handleShare}>
            <Share2 className="mr-1 inline-block h-4 w-4" />
            Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareOptionsDialog;
